import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useBuyPlans } from "../useHook";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import { Eye } from "lucide-react";
import { usePermission, SystemPermissions } from "../../../Utils/permissionAccess";

const LIMIT = 10;

export default function PurchasedHouseDesigns() {
    const navigate = useNavigate();
    const { hasPermission } = usePermission();
    const canReadAll = hasPermission(SystemPermissions.PurchasedHouseDesigns.READ_ALL_PURCHASES);
    const canReadPending = hasPermission(SystemPermissions.PurchasedHouseDesigns.READ_PENDING_VERIFICATION);
    const canReadApproved = hasPermission(SystemPermissions.PurchasedHouseDesigns.READ_APPROVED);
    const canReadRejected = hasPermission(SystemPermissions.PurchasedHouseDesigns.READ_REJECTED);
    const canViewDetail = hasPermission(SystemPermissions.PurchasedHouseDesigns.VIEW_DETAIL);

    const {
        getPurchasedHouseDesigns,
        verifyPurchasedHouseDesignPayment,
    } = useBuyPlans();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [activeTab, setActiveTab] = useState("all");
    const [loading, setLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);

    // Automatically set default tab to first allowed tab if "all" is not permitted
    useEffect(() => {
        if (activeTab === "all" && !canReadAll) {
            if (canReadPending) setActiveTab("pending");
            else if (canReadApproved) setActiveTab("approved");
            else if (canReadRejected) setActiveTab("rejected");
        }
    }, [canReadAll, canReadPending, canReadApproved, canReadRejected, activeTab]);

    const loadPurchases = async () => {
        // Guard tab loading
        if (activeTab === "all" && !canReadAll) return;
        if (activeTab === "pending" && !canReadPending) return;
        if (activeTab === "approved" && !canReadApproved) return;
        if (activeTab === "rejected" && !canReadRejected) return;

        setLoading(true);
        try {
            const payload = await getPurchasedHouseDesigns(
                {
                    offset,
                    limit: LIMIT,
                    status: activeTab,
                },
                setRows,
            );
            setTotal(payload.pagination?.totalRecords ?? 0);
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to load purchased house designs.",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPurchases();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, activeTab]);

    const handleTab = (tab) => {
        setActiveTab(tab);
        setOffset(0);
    };

    const handleDownload = (imageUrl) => {
        if (!imageUrl) return;
        const link = document.createElement("a");
        link.href = imageUrl;
        link.download = `house-design-payment-${Date.now()}.jpg`;
        link.target = "_blank";
        link.rel = "noreferrer";
        link.click();
    };

    const handleAccept = async (purchaseId) => {
        const result = await Swal.fire({
            title: "Approve payment?",
            text: `Approve payment for purchase #${purchaseId}? The customer will be notified by email.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#22c55e",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, approve",
            cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) return;

        try {
            const response = await verifyPurchasedHouseDesignPayment(
                purchaseId,
                "approved",
            );
            if (response.status === 200 && response.data?.success !== false) {
                await loadPurchases();
                Swal.fire({
                    icon: "success",
                    title: "Payment approved",
                    text: response.data?.message || "Status updated successfully.",
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Failed",
                    text: response.data?.message || "Could not update status.",
                });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    error.response?.data?.message ||
                    "An error occurred while updating the payment status.",
            });
        }
    };

    const handleReject = async (purchaseId) => {
        const result = await Swal.fire({
            title: "Reject payment?",
            text: `Reject payment for purchase #${purchaseId}? The customer will be notified by email.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, reject",
            cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) return;

        try {
            const response = await verifyPurchasedHouseDesignPayment(
                purchaseId,
                "rejected",
            );
            if (response.status === 200 && response.data?.success !== false) {
                await loadPurchases();
                Swal.fire({
                    icon: "success",
                    title: "Payment rejected",
                    text: response.data?.message || "Status updated successfully.",
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Failed",
                    text: response.data?.message || "Could not update status.",
                });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    error.response?.data?.message ||
                    "An error occurred while updating the payment status.",
            });
        }
    };

    const canVerifyRow = (row) =>
        row.payment_status === "pending" && Boolean(row.payment_screenshot);

    const renderLastColumnHeader = () => {
        if (activeTab === "pending") return "Actions";
        if (activeTab === "all") return "Status / actions";
        return "Status";
    };

    const renderLastColumnCell = (row) => {
        if (activeTab === "pending") {
            return (
                <td className="px-3 py-4 border-b text-sm">
                    {canReadPending && (
                        <button
                            type="button"
                            onClick={() => handleAccept(row.id)}
                            className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 mr-1 text-xs"
                        >
                            Approve
                        </button>
                    )}
                    {canReadPending && (
                        <button
                            type="button"
                            onClick={() => handleReject(row.id)}
                            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 text-xs"
                        >
                            Reject
                        </button>
                    )}
                </td>
            );
        }
        if (activeTab === "all") {
            if (canVerifyRow(row)) {
                return (
                    <td className="px-3 py-4 border-b text-sm">
                        {canReadPending && (
                            <button
                                type="button"
                                onClick={() => handleAccept(row.id)}
                                className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 mr-1 text-xs"
                            >
                                Approve
                            </button>
                        )}
                        {canReadPending && (
                            <button
                                type="button"
                                onClick={() => handleReject(row.id)}
                                className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 text-xs"
                            >
                                Reject
                            </button>
                        )}
                        {!canReadPending && (
                            <span className="text-amber-700 text-xs font-medium">
                                Pending
                            </span>
                        )}
                    </td>
                );
            }
            if (row.payment_status === "pending" && !row.payment_screenshot) {
                return (
                    <td className="px-3 py-4 border-b text-sm">
                        <span className="text-amber-700 text-xs font-medium">
                            Awaiting screenshot
                        </span>
                    </td>
                );
            }
            return (
                <td className="px-3 py-4 border-b text-sm capitalize">
                    {row.payment_status}
                    {row.payment_approved ? " ✓" : ""}
                </td>
            );
        }
        return (
            <td className="px-3 py-4 border-b text-sm capitalize">
                {row.payment_status}
                {row.payment_approved ? " ✓" : ""}
            </td>
        );
    };

    const renderTable = (data) => (
        <div className="overflow-x-auto mt-6">
            <table className="min-w-full bg-white shadow-md rounded-lg overflow-hidden table-fixed">
                <thead>
                    <tr className="brand-gradient text-white">
                        <th className="p-3 text-left w-10">#</th>
                        <th className="p-3 text-left">Customer</th>
                        <th className="p-3 text-left">House design</th>
                        <th className="p-3 text-left">Amount</th>
                        <th className="p-3 text-left">Garage</th>
                        <th className="p-3 text-left">Screenshot</th>
                        <th className="p-3 text-left">{renderLastColumnHeader()}</th>
                        {canViewDetail && <th className="p-3 text-left w-24">Details</th>}
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={canViewDetail ? 8 : 7} className="text-center p-8 text-gray-500">
                                Loading…
                            </td>
                        </tr>
                    ) : data.length === 0 ? (
                        <tr>
                            <td colSpan={canViewDetail ? 8 : 7} className="text-center p-8 text-gray-500">
                                No records found
                            </td>
                        </tr>
                    ) : (
                        data.map((row, index) => {
                            const user = row.user || {};
                            const hd = row.house_design || {};
                            const shotUrl = row.payment_screenshot_url || null;
                            return (
                                <tr key={row.id}>
                                    <td className="px-3 py-4 border-b text-sm">{offset + index + 1}</td>
                                    <td className="px-3 py-4 border-b text-sm">
                                        <div className="font-medium text-gray-900">
                                            {user.firstName || ""} {user.lastName || ""}
                                        </div>
                                        <div className="text-gray-500 text-xs truncate">{user.email}</div>
                                        {user.contactNo && (
                                            <div className="text-gray-400 text-xs">{user.contactNo}</div>
                                        )}
                                    </td>
                                    <td className="px-3 py-4 border-b text-sm">
                                        <span className="line-clamp-2">{hd.title || "—"}</span>
                                    </td>
                                    <td className="px-3 py-4 border-b text-sm">
                                        Rs.{" "}
                                        {row.price != null
                                            ? Number(row.price).toLocaleString()
                                            : "—"}
                                    </td>
                                    <td className="px-3 py-4 border-b text-sm capitalize">
                                        {row.garage_location || "—"}
                                    </td>
                                    <td className="px-3 py-4 border-b text-sm">
                                        {shotUrl ? (
                                            <button
                                                type="button"
                                                className="text-blue-600 hover:underline mr-2"
                                                onClick={() => setPreviewUrl(shotUrl)}
                                            >
                                                Preview
                                            </button>
                                        ) : (
                                            <span className="text-gray-400">—</span>
                                        )}
                                        {shotUrl && (
                                            <button
                                                type="button"
                                                className="text-indigo-600 hover:underline"
                                                onClick={() => handleDownload(shotUrl)}
                                            >
                                                Download
                                            </button>
                                        )}
                                    </td>
                                    {renderLastColumnCell(row)}
                                    {canViewDetail && (
                                        <td className="table-td">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    navigate(
                                                        `/purchased-house-designs/view/${row.id}`,
                                                    )
                                                }
                                                className="btn btn-view text-white"
                                            >
                                                <Eye className="text-white" /> View
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="p-6">
            <div className="bg-gray-100 p-4 flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Purchased house designs</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-6">
                {canReadAll && (
                    <button
                        type="button"
                        onClick={() => handleTab("all")}
                        className={`px-4 py-2 rounded w-full text-center ${activeTab === "all"
                            ? "btn btn-view"
                            : "btn btn-primary"
                            }`}
                    >
                        All purchases
                    </button>
                )}
                {canReadPending && (
                    <button
                        type="button"
                        onClick={() => handleTab("pending")}
                        className={`px-4 py-2 rounded w-full text-center ${activeTab === "pending"
                            ? "btn btn-view"
                            : "btn btn-primary"
                            }`}
                    >
                        Pending verification
                    </button>
                )}
                {canReadApproved && (
                    <button
                        type="button"
                        onClick={() => handleTab("approved")}
                        className={`px-4 py-2 rounded w-full text-center ${activeTab === "approved"
                            ? "btn btn-view"
                            : "btn btn-primary"
                            }`}
                    >
                        Approved
                    </button>
                )}
                {canReadRejected && (
                    <button
                        type="button"
                        onClick={() => handleTab("rejected")}
                        className={`px-4 py-2 rounded w-full text-center ${activeTab === "rejected"
                            ? "btn btn-view"
                            : "btn btn-primary"
                            }`}
                    >
                        Rejected
                    </button>
                )}
            </div>

            {renderTable(rows)}

            <div className="mt-6">
                <GlobalPagination
                    offset={offset}
                    limit={LIMIT}
                    total={total}
                    onPageChange={setOffset}
                />
            </div>

            {previewUrl && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setPreviewUrl(null)}
                >
                    <button
                        type="button"
                        className="absolute top-4 right-4 text-white text-2xl font-bold"
                        onClick={() => setPreviewUrl(null)}
                    >
                        ×
                    </button>
                    <img
                        src={previewUrl}
                        alt="Payment screenshot"
                        className="max-w-full max-h-[90vh] object-contain rounded shadow-lg"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
}
